import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Pagination,
  Menu,
  MenuItem,
  InputAdornment,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableSortLabel,
  Tooltip,
} from "@mui/material";
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  TableChart as TableChartIcon,
  FilterList as FilterListIcon,
  GetApp as GetAppIcon,
  Refresh as RefreshIcon,
  ViewColumn as ViewColumnIcon,
  Storage as StorageIcon,
  Schedule as ScheduleIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import axios from "axios";
import { Navigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { isLocalInternalHost } from "../../utils/localInternalHost";

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (
    typeof window !== "undefined" &&
    (window.location.hostname.includes("ondigitalocean.app") ||
      window.location.hostname.includes("services.knls.in"))
  ) {
    return "/api";
  }
  return "http://localhost:3000/api";
};

const API_BASE_URL = getApiBaseUrl();

const getAuthToken = () =>
  localStorage.getItem("admin_token") ||
  localStorage.getItem("token") ||
  localStorage.getItem("user_token") ||
  "";

const MyTables = () => {
  const { user } = useAuth();
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [tableSearch, setTableSearch] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [total, setTotal] = useState(0);

  // Search and filters
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("ASC");
  const [filters, setFilters] = useState({});

  // UI state
  const [anchorEl, setAnchorEl] = useState(null);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [selectedColumn, setSelectedColumn] = useState("");
  const [backupSettings, setBackupSettings] = useState({
    enabled: true,
    time: "02:00",
    emailNotification: true,
    retentionDays: 7,
  });
  const [backupSettingsLoading, setBackupSettingsLoading] = useState(false);

  // Fetch all tables and stats
  useEffect(() => {
    fetchTables();
    fetchStats();
    fetchBackupSettings();
  }, []);

  // Fetch tables when search changes
  useEffect(() => {
    fetchTables();
  }, [tableSearch]);

  // Auto-refresh tables every 30 seconds to get newly created tables
  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchTables();
      fetchStats();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(intervalId);
  }, [tableSearch]);

  // Refresh tables when window gains focus (user switches back to tab)
  useEffect(() => {
    const handleFocus = () => {
      fetchTables();
      fetchStats();
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [tableSearch]);

  // Fetch table data when table is selected or filters change
  useEffect(() => {
    if (selectedTable) {
      fetchTableData();
    }
  }, [selectedTable, page, limit, search, sortBy, sortOrder, filters]);

  const fetchTables = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      if (!token) {
        setError("Session expired. Please login again.");
        setTables([]);
        return;
      }
      // Add cache-busting timestamp to ensure fresh data
      const params = tableSearch
        ? { search: tableSearch, _t: Date.now() }
        : { _t: Date.now() };
      const response = await axios.get(`${API_BASE_URL}/tables`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data.success) {
        setTables(response.data.tables);
        setError(null);
      }
    } catch (err) {
      setTables([]);
      setError(
        "Failed to fetch tables: " +
          (err.response?.data?.message || err.message),
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        setStats(null);
        return;
      }
      // Add cache-busting timestamp to ensure fresh data
      const response = await axios.get(`${API_BASE_URL}/tables/stats`, {
        params: { _t: Date.now() },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (err) {
      console.warn("Failed to fetch stats:", err);
      setStats(null);
    }
  };

  const fetchBackupSettings = async () => {
    try {
      const token = getAuthToken();
      if (!token) {
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/backup/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data?.success && response.data?.settings) {
        setBackupSettings(response.data.settings);
      }
    } catch (err) {
      console.warn("Failed to fetch backup settings:", err);
    }
  };

  const fetchTableData = async () => {
    if (!selectedTable) return;

    try {
      setLoading(true);
      setError(null);
      const token = getAuthToken();
      if (!token) {
        setError("Session expired. Please login again.");
        setTableData([]);
        setColumns([]);
        setTotal(0);
        return;
      }

      // Add cache-busting timestamp to ensure fresh data
      const params = {
        page,
        limit,
        search,
        sortBy,
        sortOrder,
        filters: JSON.stringify(filters),
        _t: Date.now(), // Cache-busting parameter
      };

      const response = await axios.get(
        `${API_BASE_URL}/tables/${selectedTable}/data`,
        {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.data.success) {
        setTableData(response.data.data);
        setColumns(response.data.columns);
        setTotal(response.data.pagination.total);
      }
    } catch (err) {
      setError(
        "Failed to fetch table data: " +
          (err.response?.data?.message || err.message),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleTableSelect = (tableName) => {
    setSelectedTable(tableName);
    setPage(1);
    setSearch("");
    setSortBy("");
    setSortOrder("ASC");
    setFilters({});
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "ASC" ? "DESC" : "ASC");
    } else {
      setSortBy(column);
      setSortOrder("ASC");
    }
  };

  const handleDownload = async (format) => {
    if (!selectedTable) return;

    try {
      const token = getAuthToken();
      if (!token) {
        setError("Session expired. Please login again.");
        return;
      }
      const url = `${API_BASE_URL}/tables/${selectedTable}/export/${format}`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });

      // Create download link
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `${selectedTable}_${Date.now()}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      setError(
        "Failed to download: " + (err.response?.data?.message || err.message),
      );
    }
  };

  const handleDownloadEntireDatabase = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = getAuthToken();
      if (!token) {
        setError("Session expired. Please login again.");
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/backup`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });

      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      const timestamp = new Date().toISOString().split("T")[0];
      link.download = `locsy_database_backup_${timestamp}.sql`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      setError(
        "Failed to download database backup: " +
          (err.response?.data?.message || err.message),
      );
    } finally {
      setLoading(false);
    }
  };



  const handleFilterAdd = () => {
    if (selectedColumn && filters[selectedColumn] === undefined) {
      setFilters({ ...filters, [selectedColumn]: "" });
      setSelectedColumn("");
    }
  };

  const handleFilterRemove = (column) => {
    const newFilters = { ...filters };
    delete newFilters[column];
    setFilters(newFilters);
  };

  const handleFilterChange = (column, value) => {
    setFilters({ ...filters, [column]: value });
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  if (!isLocalInternalHost()) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1" fontWeight="bold">
          My Tables
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchTables}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<StorageIcon />}
            onClick={handleDownloadEntireDatabase}
            disabled={loading}
            sx={{
              bgcolor: "warning.main",
              "&:hover": { bgcolor: "warning.dark" },
            }}
          >
            Download Entire Database
          </Button>
          {selectedTable && (
            <>
              <Button
                variant="outlined"
                startIcon={<GetAppIcon />}
                onClick={handleMenuOpen}
              >
                Download Table
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
              >
                <MenuItem
                  onClick={() => {
                    handleDownload("sql");
                    handleMenuClose();
                  }}
                >
                  Download as SQL
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleDownload("excel");
                    handleMenuClose();
                  }}
                >
                  Download as Excel
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleDownload("pdf");
                    handleMenuClose();
                  }}
                >
                  Download as PDF
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: "primary.main", color: "primary.contrastText" }}>
            <CardContent>
              <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                Total Tables
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {stats ? (
                  stats.total_tables
                ) : (
                  <CircularProgress size={24} color="inherit" />
                )}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Database tables
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: "success.main", color: "success.contrastText" }}>
            <CardContent>
              <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                Total Records
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {stats ? (
                  stats.total_records.toLocaleString()
                ) : (
                  <CircularProgress size={24} color="inherit" />
                )}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Across all tables
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: "info.main", color: "info.contrastText" }}>
            <CardContent>
              <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                Records Today
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {stats ? (
                  stats.records_today.toLocaleString()
                ) : (
                  <CircularProgress size={24} color="inherit" />
                )}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Inserted today
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: "warning.main", color: "warning.contrastText" }}>
            <CardContent>
              <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                Database Size
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {stats ? (
                  stats.database_size_bytes > 0 ? (
                    (stats.database_size_bytes / 1024 / 1024).toFixed(2) + " MB"
                  ) : (
                    "N/A"
                  )
                ) : (
                  <CircularProgress size={24} color="inherit" />
                )}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Total size
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Auto Backup Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <ScheduleIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">
              Auto Backup Settings
            </Typography>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    Enable Auto Backup
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Automatically backup database daily
                  </Typography>
                </Box>
                <IconButton
                  onClick={() =>
                    setBackupSettings({
                      ...backupSettings,
                      enabled: !backupSettings.enabled,
                    })
                  }
                  color={backupSettings.enabled ? "primary" : "default"}
                >
                  {backupSettings.enabled ? (
                    <ToggleOnIcon sx={{ fontSize: 40 }} />
                  ) : (
                    <ToggleOffIcon sx={{ fontSize: 40 }} />
                  )}
                </IconButton>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Backup Time (IST)"
                type="time"
                value={backupSettings.time}
                onChange={(e) =>
                  setBackupSettings({ ...backupSettings, time: e.target.value })
                }
                disabled={!backupSettings.enabled}
                helperText="Daily backup will run at this time (IST)"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    Email Notification
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Send backup via email after completion
                  </Typography>
                </Box>
                <IconButton
                  onClick={() =>
                    setBackupSettings({
                      ...backupSettings,
                      emailNotification: !backupSettings.emailNotification,
                    })
                  }
                  color={
                    backupSettings.emailNotification ? "primary" : "default"
                  }
                  disabled={!backupSettings.enabled}
                >
                  {backupSettings.emailNotification ? (
                    <ToggleOnIcon sx={{ fontSize: 40 }} />
                  ) : (
                    <ToggleOffIcon sx={{ fontSize: 40 }} />
                  )}
                </IconButton>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Retention Days"
                type="number"
                value={backupSettings.retentionDays}
                onChange={(e) =>
                  setBackupSettings({
                    ...backupSettings,
                    retentionDays: parseInt(e.target.value) || 7,
                  })
                }
                disabled={!backupSettings.enabled}
                helperText="Keep backups for this many days"
                inputProps={{ min: 1, max: 30 }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setBackupSettings({
                      enabled: true,
                      time: "02:00",
                      emailNotification: true,
                      retentionDays: 7,
                    });
                  }}
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SettingsIcon />}
                  onClick={async () => {
                    setBackupSettingsLoading(true);
                    try {
                      const token = getAuthToken();
                      if (!token) {
                        setError("Session expired. Please login again.");
                        return;
                      }
                      await axios.post(
                        `${API_BASE_URL}/backup/settings`,
                        backupSettings,
                        { headers: { Authorization: `Bearer ${token}` } },
                      );
                      setError(null);
                      alert("Backup settings saved successfully!");
                    } catch (err) {
                      setError(
                        "Failed to save backup settings: " +
                          (err.response?.data?.message || err.message),
                      );
                    } finally {
                      setBackupSettingsLoading(false);
                    }
                  }}
                  disabled={backupSettingsLoading}
                >
                  {backupSettingsLoading ? "Saving..." : "Save Settings"}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Tables List */}
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Database Tables
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Search tables..."
                value={tableSearch}
                onChange={(e) => setTableSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />
              <Box sx={{ maxHeight: "70vh", overflowY: "auto" }}>
                {loading && !selectedTable ? (
                  <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  tables.map((table) => (
                    <Box
                      key={table.table_name}
                      onClick={() => handleTableSelect(table.table_name)}
                      sx={{
                        p: 1.5,
                        mb: 1,
                        cursor: "pointer",
                        borderRadius: 1,
                        bgcolor:
                          selectedTable === table.table_name
                            ? "primary.light"
                            : "transparent",
                        "&:hover": {
                          bgcolor: "action.hover",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={
                            selectedTable === table.table_name
                              ? "bold"
                              : "normal"
                          }
                        >
                          {table.table_name}
                        </Typography>
                        <Chip label={table.column_count} size="small" />
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Table Data Viewer */}
        <Grid item xs={12} md={9}>
          {selectedTable ? (
            <Card>
              <CardContent>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Table: {selectedTable}
                  </Typography>

                  {/* Search and Filters */}
                  <Box
                    sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}
                  >
                    <TextField
                      placeholder="Search..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ flexGrow: 1, minWidth: 200 }}
                    />

                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Rows</InputLabel>
                      <Select
                        value={limit}
                        label="Rows"
                        onChange={(e) => {
                          setLimit(e.target.value);
                          setPage(1);
                        }}
                      >
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                        <MenuItem value={200}>200</MenuItem>
                      </Select>
                    </FormControl>

                    <Button
                      variant="outlined"
                      startIcon={<FilterListIcon />}
                      onClick={() => setFilterDialogOpen(true)}
                    >
                      Filters
                    </Button>
                  </Box>

                  {/* Active Filters */}
                  {Object.keys(filters).length > 0 && (
                    <Box
                      sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}
                    >
                      {Object.entries(filters).map(([column, value]) => (
                        <Chip
                          key={column}
                          label={`${column}: ${value || "any"}`}
                          onDelete={() => handleFilterRemove(column)}
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  )}

                  {/* Table */}
                  {loading ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", p: 4 }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : (
                    <>
                      <TableContainer
                        component={Paper}
                        sx={{ maxHeight: "60vh" }}
                      >
                        <Table stickyHeader size="small">
                          <TableHead>
                            <TableRow>
                              {columns.map((column) => (
                                <TableCell key={column}>
                                  <TableSortLabel
                                    active={sortBy === column}
                                    direction={
                                      sortBy === column
                                        ? sortOrder.toLowerCase()
                                        : "asc"
                                    }
                                    onClick={() => handleSort(column)}
                                  >
                                    {column}
                                  </TableSortLabel>
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {tableData.length === 0 ? (
                              <TableRow>
                                <TableCell
                                  colSpan={columns.length}
                                  align="center"
                                >
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    No data found
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ) : (
                              tableData.map((row, index) => (
                                <TableRow key={index} hover>
                                  {columns.map((column) => (
                                    <TableCell key={column}>
                                      {row[column] !== null &&
                                      row[column] !== undefined
                                        ? String(row[column]).substring(0, 100)
                                        : "NULL"}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {/* Pagination */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mt: 2,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Showing {(page - 1) * limit + 1} to{" "}
                          {Math.min(page * limit, total)} of {total} rows
                        </Typography>
                        <Pagination
                          count={Math.ceil(total / limit)}
                          page={page}
                          onChange={(e, value) => setPage(value)}
                          color="primary"
                        />
                      </Box>
                    </>
                  )}
                </Box>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent>
                <Box sx={{ textAlign: "center", py: 8 }}>
                  <TableChartIcon
                    sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
                  />
                  <Typography variant="h6" color="text.secondary">
                    Select a table from the list to view its data
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Filter Dialog */}
      <Dialog
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Filter</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Column</InputLabel>
              <Select
                value={selectedColumn}
                label="Column"
                onChange={(e) => setSelectedColumn(e.target.value)}
              >
                {columns
                  .filter((col) => !filters.hasOwnProperty(col))
                  .map((col) => (
                    <MenuItem key={col} value={col}>
                      {col}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>

          {Object.keys(filters).length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Active Filters:
              </Typography>
              {Object.entries(filters).map(([column, value]) => (
                <Box
                  key={column}
                  sx={{ display: "flex", gap: 1, mt: 1, alignItems: "center" }}
                >
                  <Typography variant="body2" sx={{ minWidth: 120 }}>
                    {column}:
                  </Typography>
                  <TextField
                    size="small"
                    value={value}
                    onChange={(e) => handleFilterChange(column, e.target.value)}
                    placeholder="Filter value"
                    fullWidth
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleFilterRemove(column)}
                    color="error"
                  >
                    ×
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFilterDialogOpen(false)}>Close</Button>
          <Button
            onClick={handleFilterAdd}
            variant="contained"
            disabled={!selectedColumn}
          >
            Add Filter
          </Button>
        </DialogActions>
      </Dialog>


    </Box>
  );
};

export default MyTables;
